import React from 'react';
import PropTypes from 'prop-types';
import { I18nText, Amount, EmphasisTag } from '@welldev-group/react-comp-lib';
import Icon from '@welldev-group/rcl-icon';
import Tooltip from '@welldev-group/rcl-tooltip';
import DateTime from '../DateTime';

import InvoicesTableRowShowMore from './InvoicesTableRowShowMore';

const InvoicesTable = ({ invoices, canRecordWireTransfer, showAll }) => {
  if (invoices.length === 0) {
    return <I18nText id="billing.transactions.list.no_pending_invoices" />;
  }

  const invoiceTable = () =>
    invoices.map(({ invoice, balance }) => {
      return (
        <div className="billing-transaction-list__row" key={invoice.id}>
          <div className="grid">
            <div className="col-lg-1 col-md-11 col-9 order-1 billing-transaction-list__row-item">
              <DateTime dateTime={invoice.createdAt} format="short" />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.date" />
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-4 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-title">
              {invoice.lineItemTitle}
              <EmphasisTag
                type="null"
                size="small"
                text={I18n.t(
                  `billing.welltravel_boooking.transactions.emphasis.${
                    invoice.reason ? 'price_increased' : 'wire_transfer_payment'
                  }`,
                )}
              />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.title" />
              </div>
            </div>
            <div className="col-lg-1 col-md-4 col-sm-4 col-6 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-icon">
              <Tooltip
                content={I18n.t(
                  `billing.welltravel_boooking.transactions.emphasis.tooltip.${
                    invoice.reason ? 'wallet' : 'wire_transfer'
                  }`,
                )}
                type="inverse"
                position="bottom"
                size="tiny"
              >
                <Icon
                  name={invoice.reason ? 'walletBalance' : 'wireTransfer'}
                  showBGColor={true}
                  color="tertiary"
                  size="normal"
                />
              </Tooltip>
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.method" />
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-4 col-6 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-amount">
              <Amount locale="de-CH" value={invoice.amount} currency={invoice.currency} />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.amount" />
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-4 col-12 order-3 billing-transaction-list__row-item">
              <Amount locale="de-CH" value={balance.after} currency={balance.currency} />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.balance" />
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-4 col-12 order-3 billing-transaction-list__row-item">
              <Amount
                locale="de-CH"
                value={balance.reservedBalanceTill}
                currency={balance.currency}
              />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.reserved" />
              </div>
            </div>
            <div className="col-lg-1 col-md-1 col-3 order-lg-4 order-2 billing-transaction-list__row-item billing-transaction-list__row-item--actions">
              <InvoicesTableRowShowMore
                invoice={invoice}
                canRecordWireTransfer={canRecordWireTransfer}
              />
            </div>
          </div>
        </div>
      );
    });

  return (
    <div className="billing-transaction-list__table col-12">
      <div className="billing-transaction-list__row billing-transaction-list__row--header">
        <div className="grid">
          <div className="col-lg-1 col-4 order-1 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.date" />
          </div>
          <div className="col-lg-3 col-11 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.title" />
          </div>
          <div className="col-lg-1 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.method" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.amount" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.balance" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.reserved" />
          </div>
          <div className="col-1 order-lg-4 order-2 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.detail" />
          </div>
        </div>
      </div>
      {showAll ? invoiceTable() : invoiceTable().slice(0, 5)}
    </div>
  );
};

InvoicesTable.defaultProps = {
  canRecordWireTransfer: false,
  showAll: true,
};

InvoicesTable.propTypes = {
  canRecordWireTransfer: PropTypes.bool,
  invoices: PropTypes.arrayOf(
    PropTypes.shape({
      invoice: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        amount: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        showDetailsUrl: PropTypes.string.isRequired,
      }).isRequired,
      balance: PropTypes.shape({
        after: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  showAll: PropTypes.bool,
};

export default InvoicesTable;
